import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Tour, StepProps } from '@/components/tour'
import { Box, Stack } from '@/components/structure'
import { Label } from '@/components/text'
import { Button } from '@/components/button'
import { colors } from '@/styles'
import useDelayedMount from '@/hooks/use-delayed-mount'
import { useHighlight } from '@/models/toaster/hooks'

type HighlightProps = {
  children?: React.ReactNode
  hide?: boolean
  link?: string
  onClick: () => {}
}

const ApplicationContent: React.FC<{ next: () => void; cancel: () => void }> = ({
  next,
  cancel
}) => {
  const { t } = useTranslation()
  return (
    <Stack gutter={16} maxWidth={216}>
      <Stack gutter={4}>
        <Label fontSize="14px">
          {t(
            'b.toaster.view.application.trust_score.view_application.tour.title',
            'Review applicants'
          )}
        </Label>
        <Box color={colors.grey32}>
          <Label fontSize="12px" type="small">
            {t(
              'b.toaster.view.application.trust_score.view_application.tour.description',
              'This is where you can view each applicant’s tenancy application details, which includes their Trust Score.'
            )}
          </Label>
        </Box>
      </Stack>
      <Box width="100%" display="flex" justify="flex-end">
        <Box width={96}>
          <Button size="small" onClick={next}>
            {t('b.toaster.view.key_features.tour.button', 'Got it!')}
          </Button>
        </Box>
      </Box>
    </Stack>
  )
}

const applicationSteps: StepProps = [
  {
    selector: '.view-application-btn-0',
    content: props => <ApplicationContent {...props} />
  }
]

export const ApplicationHighlight: React.FC<HighlightProps> = ({ children, onClick }) => {
  const { hide, next: onBeforeClose } = useHighlight('application.trust_score', 'view_application')
  const delay = useDelayedMount(1000)
  return (
    <Tour
      hide={hide || !delay}
      steps={applicationSteps}
      position="left"
      showCloseButton={false}
      onBeforeClose={onBeforeClose}
      allowInteraction
    >
      {children}
    </Tour>
  )
}

const ContractContent: React.FC<{ next: () => void; cancel: () => void }> = ({ next, cancel }) => {
  const { t } = useTranslation()
  return (
    <Stack gutter={16} maxWidth={216}>
      <Stack gutter={4}>
        <Label fontSize="14px">
          {t(
            'b.toaster.view.complete_contract.create_contract.view_application.tour.title',
            'Review applications'
          )}
        </Label>
        <Box color={colors.grey32}>
          <Label fontSize="12px" type="small">
            {t(
              'b.toaster.view.complete_contract.create_contract.view_application.tour.description',
              'Select your best applicant and create a contract directly through their application.'
            )}
          </Label>
        </Box>
      </Stack>
      <Box width="100%" display="flex" justify="flex-end">
        <Box>
          <Button size="small" theme="text" onClick={cancel}>
            {t('common.skip', 'Skip')}
          </Button>
        </Box>
        <Box width={96}>
          <Button size="small" onClick={next}>
            {t('common.next', 'Next')}
          </Button>
        </Box>
      </Box>
    </Stack>
  )
}

const contractSteps: StepProps = [
  {
    selector: '.view-application-btn-0',
    content: props => <ContractContent {...props} />
  }
]

export const ContractHighlight: React.FC<HighlightProps> = ({ children, hide: propHide, link }) => {
  const { hide, next, skip } = useHighlight(
    'complete_contract.create_contract',
    'view_application',
    'create'
  )
  const history = useHistory()
  const delay = useDelayedMount(1000)
  const onBeforeClose = (canceled: boolean) => {
    if (!canceled) {
      next()
    } else {
      skip()
    }
    history.push(link || '/')
  }
  return (
    <Tour
      hide={hide || !delay || propHide}
      steps={contractSteps}
      position="left"
      showCloseButton
      onBeforeClose={onBeforeClose}
    >
      {children}
    </Tour>
  )
}
